import React, { useEffect, useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import type { DashboardWidgetInstance, IndicatorFilterOperator } from './types';
import { useWebMapContext } from '../webMap/WebMapContext';

const DEFAULT_MIDDLE = 'Count: {value}';

const valuePop = keyframes`
  0% { transform: scale(1); }
  40% { transform: scale(1.06); }
  100% { transform: scale(1); }
`;

function applyTemplate(tpl: string | undefined, count: number): string {
  const s = tpl?.trim() ?? '';
  if (!s) return '';
  return s.split('{value}').join(String(count));
}

const IndicatorWidget: React.FC<{ widgetConfig?: DashboardWidgetInstance['config'] }> = ({ widgetConfig }) => {
  const webMapCtx = useWebMapContext();
  const [pop, setPop] = useState(false);
  const prev = React.useRef<number | null>(null);

  const dataBinding = widgetConfig?.dataBinding;
  const isBound = Boolean(dataBinding?.boundMapWidgetId && dataBinding?.boundLayerId);

  const field = widgetConfig?.indicatorFilterField;
  const op: IndicatorFilterOperator = widgetConfig?.indicatorFilterOperator ?? 'eq';
  const filterVal = widgetConfig?.indicatorFilterValue?.trim();

  const count = useMemo(() => {
    if (!isBound || !webMapCtx || !dataBinding?.boundLayerId) return 0;

    const layerId = dataBinding.boundLayerId;
    const bindField = dataBinding.field;
    const agg = dataBinding.aggregation ?? 'count';

    let features = webMapCtx.getFeaturesForLayer(layerId);

    if (field && (filterVal || op === 'is_empty' || op === 'is_not_empty')) {
      features = features.filter((f) => {
        const raw = f.properties?.[field];
        const actual = String(raw ?? '').trim().toLowerCase();
        const target = (filterVal ?? '').trim().toLowerCase();

        switch (op) {
          case 'eq':
            return actual === target;
          case 'neq':
            return actual !== target;
          case 'contains':
            return actual.includes(target);
          case 'not_contains':
            return !actual.includes(target);
          case 'starts_with':
            return actual.startsWith(target);
          case 'ends_with':
            return actual.endsWith(target);
          case 'gt':
            return Number(raw) > Number(filterVal);
          case 'lt':
            return Number(raw) < Number(filterVal);
          case 'gte':
            return Number(raw) >= Number(filterVal);
          case 'lte':
            return Number(raw) <= Number(filterVal);
          case 'is_empty':
            return raw == null || actual === '';
          case 'is_not_empty':
            return raw != null && actual !== '';
          default:
            return actual === target;
        }
      });
    }

    if (!bindField || agg === 'count') return features.length;

    const nums = features
      .map((f) => Number(f.properties?.[bindField]))
      .filter((n) => !Number.isNaN(n));

    if (nums.length === 0) return 0;
    switch (agg) {
      case 'sum':
        return nums.reduce((a, b) => a + b, 0);
      case 'avg':
        return nums.reduce((a, b) => a + b, 0) / nums.length;
      case 'min':
        return Math.min(...nums);
      case 'max':
        return Math.max(...nums);
      default:
        return features.length;
    }
  }, [isBound, webMapCtx, dataBinding, field, op, filterVal]);

  useEffect(() => {
    if (prev.current === null) {
      prev.current = count;
      return undefined;
    }
    if (prev.current !== count) {
      setPop(true);
      const t = window.setTimeout(() => setPop(false), 500);
      prev.current = count;
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [count]);

  if (!isBound) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1, p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">No data source</Typography>
        <Typography variant="caption" color="text.secondary">
          Bind this widget to a Web Map layer in widget settings to display a statistic.
        </Typography>
      </Box>
    );
  }

  const top = applyTemplate(widgetConfig?.indicatorTextTop, count);
  const middle = applyTemplate(widgetConfig?.indicatorTextMiddle?.trim() || DEFAULT_MIDDLE, count);
  const bottom = applyTemplate(widgetConfig?.indicatorTextBottom, count);

  const cTop = widgetConfig?.indicatorColorTop?.trim() || 'rgba(255,255,255,0.45)';
  const cMid = widgetConfig?.indicatorColorMiddle?.trim() || '#69f0ae';
  const cBot = widgetConfig?.indicatorColorBottom?.trim() || 'rgba(255,255,255,0.35)';

  const showUpdated = widgetConfig?.indicatorShowLastUpdated !== false;

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 1, sm: 1.25 },
        py: { xs: 0.75, sm: 1 },
        overflow: 'hidden',
      }}
    >
      <Stack spacing={0.5} sx={{ width: '100%', textAlign: 'center', alignItems: 'center' }}>
        {top ? (
          <Typography variant="caption" sx={{ color: cTop, fontWeight: 500, letterSpacing: '0.06em' }}>
            {top}
          </Typography>
        ) : null}
        <Typography
          variant="h4"
          component="div"
          sx={{
            color: cMid,
            fontWeight: 800,
            letterSpacing: '-0.02em',
            lineHeight: 1.15,
            fontFeatureSettings: '"tnum"',
            animation: pop ? `${valuePop} 0.45s ease-out` : 'none',
            textAlign: 'center',
            fontSize: { xs: '1.35rem', sm: '1.5rem' },
          }}
        >
          {middle}
        </Typography>
        {bottom ? (
          <Typography variant="caption" sx={{ color: cBot }}>
            {bottom}
          </Typography>
        ) : null}
        {showUpdated && (
          <Typography variant="caption" sx={{ color: 'text.disabled', opacity: 0.85 }}>
            Updated {new Date().toLocaleTimeString()}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default IndicatorWidget;
